import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 8;
const MARGIN = 8;

/**
 * Anchored floating panel for the date/time pickers.
 *
 * Portals into the nearest ancestor `.modal` when there is one, rather than
 * straight to `document.body` — most of these pickers live inside reactstrap
 * modals, and a body-level portal ends up outside the modal's focus trap.
 */
const Popover = ({ anchorRef, open, onClose, id, className = '', labelledBy, children }) => {
    const panelRef = useRef(null);
    const [container, setContainer] = useState(null);
    const [position, setPosition] = useState(null);

    useLayoutEffect(() => {
        if (!open) {
            setContainer(null);
            setPosition(null);
            return;
        }
        setContainer(anchorRef.current?.closest('.modal') || document.body);
    }, [open, anchorRef]);

    const reposition = useCallback(() => {
        const anchor = anchorRef.current?.getBoundingClientRect();
        const panel = panelRef.current?.getBoundingClientRect();
        if (!anchor || !panel) return;

        let top = anchor.bottom + GAP;
        if (top + panel.height > window.innerHeight - MARGIN) {
            const above = anchor.top - GAP - panel.height;
            top = above >= MARGIN
                ? above
                : Math.max(MARGIN, window.innerHeight - MARGIN - panel.height);
        }

        const left = Math.min(
            Math.max(MARGIN, anchor.left),
            Math.max(MARGIN, window.innerWidth - MARGIN - panel.width)
        );

        setPosition({ top, left });
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (!open || !container) return undefined;

        reposition();
        // Capture phase so the panel keeps up with scrolling inside modal bodies
        // and other overflow containers, not just the window.
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);

        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open, container, reposition]);

    useEffect(() => {
        if (!open) return undefined;

        const handlePointerDown = (event) => {
            if (panelRef.current?.contains(event.target)) return;
            if (anchorRef.current?.contains(event.target)) return; // let the trigger toggle
            onClose('outside');
        };

        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            // Capture + stopPropagation, otherwise Escape closes the surrounding
            // reactstrap modal instead of just this panel.
            event.stopPropagation();
            onClose('escape');
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [open, onClose, anchorRef]);

    if (!open || !container) return null;

    return createPortal(
        <div
            ref={panelRef}
            id={id}
            role="dialog"
            aria-labelledby={labelledBy}
            className={`dt-popover ${position ? 'is-placed' : ''} ${className}`.trim()}
            style={{ top: position?.top ?? 0, left: position?.left ?? 0 }}
        >
            {children}
        </div>,
        container
    );
};

export default Popover;
